import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api, formatDate, formatDateTime } from '../utils/api'
import { OrderDetailModal } from './shared'
import DealDetailModal from './deals/DealDetailModal'
import ResizeHandle from './tickets/helpers/ResizeHandle.jsx'
import EmailContent from './tickets/helpers/EmailContent.jsx'
import ReverbThreadSection from './tickets/helpers/ReverbThreadSection.jsx'
import AttachmentThumbnail from './tickets/helpers/AttachmentThumbnail.jsx'
import DownloadAllAttachments from './tickets/helpers/DownloadAllAttachments.jsx'
import CustomerModal from './tickets/helpers/CustomerModal.jsx'
import TicketsInbox from './tickets/TicketsInbox.jsx'
import TicketConversation from './tickets/TicketConversation.jsx'
import TicketContext from './tickets/TicketContext.jsx'
import DesktopTicketsLayout from './tickets/DesktopTicketsLayout.jsx'
import MobileTicketsLayout from './tickets/MobileTicketsLayout.jsx'
import TabletTicketsLayout from './tickets/TabletTicketsLayout.jsx'
import useBreakpoint from '../hooks/useBreakpoint.jsx'
import useTicketsViewParams from './tickets/useTicketsViewParams.js'
import { useTicketPresence } from './tickets/useTicketPresence.js'
import { resolveCaseOrder, caseProduct } from './tickets/caseOrder.js'
import TopicCountsModal from './tickets/TopicCountsModal.jsx'
import { TICKET_TOPICS } from '../../../shared/ticketTopics.js'
import '../styles/pages.css'

// Tickets per list request; "Load more" fetches the next page of the tab.
const TICKET_PAGE_SIZE = 50

/**
 * Tickets - Customer support inbox tailored for Audio Trader
 *
 * Features:
 * - Gmail sync with auto-filtering
 * - AI template matching for quick responses
 * - Auto-linking to deals
 * - Customer history (previous tickets + deals)
 * - Internal notes (staff-only)
 * - Bulk actions for efficiency
 * - Priority auto-detection
 */
function Tickets() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [tickets, setTickets] = useState([])
  const [counts, setCounts] = useState({ new: 0, awaiting_reply: 0, waiting_customer: 0, waiting_third_party: 0, needs_reply: 0, waiting: 0, resolved: 0, spam: 0, service: 0, offers: 0 })
  const [lastSyncAt, setLastSyncAt] = useState(null)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [syncStatus, setSyncStatus] = useState(null)
  const [activeTab, setActiveTab] = useState('needs_reply')
  const [showMoreDropdown, setShowMoreDropdown] = useState(false)
  const moreDropdownRef = useRef(null)
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [ticketDetails, setTicketDetails] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [search, setSearch] = useState('')

  // Reverb conversation thread (lazy-loaded for channel === 'reverb' tickets)
  const [reverbThread, setReverbThread] = useState(null)
  const [reverbThreadStatus, setReverbThreadStatus] = useState('idle') // idle | loading | ready | unavailable | error
  const [reverbThreadReason, setReverbThreadReason] = useState(null)
  const [reverbThreadFetchedAt, setReverbThreadFetchedAt] = useState(null)
  const [reverbThreadRefreshing, setReverbThreadRefreshing] = useState(false)

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [bulkMode, setBulkMode] = useState(false)

  // Reply state
  const [replyText, setReplyText] = useState('')
  // Set when /reply answered 409 stale_reply: the ticket moved on since we
  // loaded it. Cleared on dismiss, on switching ticket, and by a send.
  const [staleReply, setStaleReply] = useState(null)

  // Presence heartbeat — tells the server where I am (and whether I have a
  // draft open), brings back where the other operator is, and a token that
  // moves whenever any ticket changes. The token drives a SILENT list
  // refresh: rows and counts swap underneath, the open ticket and the
  // reply draft are never touched.
  const composing = replyText.trim().length > 0
  const { viewersByTicket, activityToken } = useTicketPresence({ ticketId: selectedTicket?.id ?? null, composing })
  const seenActivityToken = useRef(null)
  const refreshDeferredByBulk = useRef(false)
  const listRequest = useRef(0)
  const [hasMoreTickets, setHasMoreTickets] = useState(false)
  const [loadingMoreTickets, setLoadingMoreTickets] = useState(false)
  useEffect(() => {
    if (activityToken == null) return
    const firstObservation = seenActivityToken.current == null
    seenActivityToken.current = activityToken
    if (firstObservation) return
    // Rows vanishing under a bulk selection would be confusing — hold the
    // refresh until bulk mode ends.
    if (bulkMode) { refreshDeferredByBulk.current = true; return }
    refreshTicketsSilently()
  }, [activityToken])
  useEffect(() => {
    if (bulkMode || !refreshDeferredByBulk.current) return
    refreshDeferredByBulk.current = false
    refreshTicketsSilently()
  }, [bulkMode])
  // Phase 3.7 — composer expand state on phone. Hoisted to the orchestrator
  // so MobileTicketsLayout can hide its bottom tab bar when the composer
  // takes the screen.
  const [composerOpen, setComposerOpen] = useState(false)
  // Tab state for MobileTicketsLayout is hoisted here so re-tapping the
  // currently-selected ticket reliably switches to the Conversation tab —
  // an effect on `activeTicketId` won't fire when the same ticket id is
  // selected twice in a row, which was the original bug. Owning the tab
  // means selectTicket can drive the switch unconditionally.
  const [mobileTab, setMobileTab] = useState('inbox')
  // Auto-exit bulk mode when the operator navigates off the inbox tab.
  // The bulk action bar is rendered inside TicketsInbox; leaving the
  // tab made the bar disappear while bulkMode stayed true, which
  // confused row taps (they toggled selection instead of opening).
  useEffect(() => {
    if (mobileTab !== 'inbox') {
      setBulkMode(false)
      setSelectedIds(new Set())
    }
  }, [mobileTab])
  const [sending, setSending] = useState(false)
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [replyFile, setReplyFile] = useState(null)

  // Customer history
  const [customerHistory, setCustomerHistory] = useState(null)
  const [loadingHistory, setLoadingHistory] = useState(false)

  // Order/Deal detail modals from side panel
  const [sidebarOrderModal, setSidebarOrderModal] = useState(null)
  const [sidebarDealModal, setSidebarDealModal] = useState(null)
  const [loadingSidebarOrder, setLoadingSidebarOrder] = useState(null)

  // Sidebar pin state
  const [pinnedItems, setPinnedItems] = useState({}) // "deal:123" => pinId
  const [togglingPin, setTogglingPin] = useState(null)

  // Internal notes
  const [notes, setNotes] = useState([])
  const [newNote, setNewNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)

  // Case flow
  const [returnRequest, setReturnRequest] = useState(null)
  const [returnReasons, setReturnReasons] = useState([])
  const [showReturnForm, setShowReturnForm] = useState(false)
  const [returnFormData, setReturnFormData] = useState({ reason_id: '', reason_notes: '' })
  const [returnLoading, setReturnLoading] = useState(false)
  const [override30DayCase, setOverride30DayCase] = useState(false)
  // Order lines for ticking the case's item(s) at opening (spec 20261008)
  const [caseLines, setCaseLines] = useState(null)
  const [caseLineRefs, setCaseLineRefs] = useState([])
  const [caseOrderId, setCaseOrderId] = useState(null)

  // Filter modal
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [filters, setFilters] = useState([])
  const [filterSearch, setFilterSearch] = useState('')
  const [newFilter, setNewFilter] = useState({
    filter_type: 'email',
    pattern: '',
    match_type: 'exact',
    action: 'block',
    reason: '',
    subject_pattern: '',
    subject_match_type: 'contains'
  })
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Customer lookup modal
  const [customerEmail, setCustomerEmail] = useState(null)

  // Pin modal
  const [showPinModal, setShowPinModal] = useState(false)
  const [pinData, setPinData] = useState({ reason: '', dueDate: '', assignedTo: '' })
  const [pinUsers, setPinUsers] = useState([])

  // Signature modal
  const [showSignatureModal, setShowSignatureModal] = useState(false)
  const [signature, setSignature] = useState('')
  const [savingSignature, setSavingSignature] = useState(false)

  // Template management
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [templateForm, setTemplateForm] = useState({ name: '', body: '', keywords: '' })
  const [savingTemplate, setSavingTemplate] = useState(false)

  // Menu dropdown
  const [showMenuDropdown, setShowMenuDropdown] = useState(false)
  const menuDropdownRef = useRef(null)

  // Sync failures
  const [showSyncFailuresModal, setShowSyncFailuresModal] = useState(false)
  const [syncFailures, setSyncFailures] = useState([])
  const [syncFailureCounts, setSyncFailureCounts] = useState({ failed: 0, abandoned: 0, total: 0 })
  const [loadingSyncFailures, setLoadingSyncFailures] = useState(false)
  const [retryingSyncFailures, setRetryingSyncFailures] = useState(false)

  // Assignment
  const [assignmentFilter, setAssignmentFilter] = useState('all')
  // 'all', 'none' (no topic yet) or a topic key from shared/ticketTopics.js
  const [topicFilter, setTopicFilter] = useState('all')
  const [showTopicCounts, setShowTopicCounts] = useState(false)
  const [ticketUsers, setTicketUsers] = useState([])

  // Close menu dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuDropdownRef.current && !menuDropdownRef.current.contains(e.target)) {
        setShowMenuDropdown(false)
      }
    }
    if (showMenuDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenuDropdown])

  // Close "More" dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (moreDropdownRef.current && !moreDropdownRef.current.contains(e.target)) {
        setShowMoreDropdown(false)
      }
    }
    if (showMoreDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMoreDropdown])

  // Responsive layout
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)
  const [leftPanelWidth, setLeftPanelWidth] = useState(350)
  const [rightPanelWidth, setRightPanelWidth] = useState(280)
  const [quickRepliesHeight, setQuickRepliesHeight] = useState(320)
  const [mobileView, setMobileView] = useState('list') // 'list' | 'detail' | 'sidebar'
  const mainContainerRef = useRef(null)
  const [layoutHeight, setLayoutHeight] = useState(null)

  useEffect(() => {
    loadTickets()
    loadTemplates()
    loadSyncStatus()
    // Load return reasons once
    if (returnReasons.length === 0) {
      api.get('/api/orders/returns/reasons').then(res => {
        setReturnReasons(res.reasons || [])
      }).catch(() => {})
    }
  }, [activeTab, search, assignmentFilter, topicFilter])

  useEffect(() => {
    loadTicketUsers()
  }, [])

  // Check for ticketId in URL to auto-open a specific ticket
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const ticketIdParam = params.get('ticketId')
    if (ticketIdParam) {
      loadTicketById(ticketIdParam)
      // Clean up URL
      navigate(location.pathname, { replace: true })
    }
  }, [location.search])

  async function loadTicketById(ticketId) {
    try {
      const response = await api.get(`/api/tickets/${ticketId}`)
      if (response.ticket) {
        setSelectedTicket(response.ticket)
        setTicketDetails(response)
        // Switch to the appropriate tab based on ticket status
        if (response.ticket.status) {
          const statusToTab = {
            'new': 'needs_reply',
            'awaiting_reply': 'needs_reply',
            'waiting_customer': 'waiting',
            'waiting_third_party': 'waiting',
            'resolved': 'resolved',
            'spam': 'spam',
            'service': 'service',
            'offers': 'offers'
          }
          setActiveTab(statusToTab[response.ticket.status] || 'needs_reply')
        }
        // Mark as read in Gmail when viewing
        api.post(`/api/tickets/${ticketId}/mark-read`).catch(err => {
          console.error('Failed to mark ticket as read in Gmail:', err)
        })
      }
    } catch (error) {
      console.error('Failed to load ticket:', error)
    }
  }

  // Window resize tracking for responsive layout
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Measure layout height — constrain ticketsLayout to remaining viewport space
  useEffect(() => {
    if (!mainContainerRef.current) return
    const top = mainContainerRef.current.getBoundingClientRect().top
    setLayoutHeight(window.innerHeight - top)
  }, [windowWidth])

  // Responsive breakpoints — single source of truth in useBreakpoint().
  // `windowWidth` above is intentionally kept for the layout-height effect,
  // which needs continuous resize tracking (not just band transitions).
  // The 768/1024 boundaries here moved to 700/1024 per spec D1 — devices in
  // the 700–767px range now resolve to `tablet` instead of `mobile`.
  const breakpoint = useBreakpoint()
  const isMobile = breakpoint === 'phone'
  const isTablet = breakpoint === 'tablet'
  const isDesktop = breakpoint === 'desktop'
  // Phase 3+4 rollout flag (?mobile-v2=1 or localStorage mirror).
  // When this is true AND we're on phone or tablet, mount the new
  // layouts. Desktop is never affected.
  const { mobileV2 } = useTicketsViewParams()
  const useMobileV2 = mobileV2 && (isMobile || isTablet)
  // Tablet focus mode can be temporarily expanded so the operator can
  // re-scan the queue while keeping the ticket open. Cleared on
  // selectTicket so picking a different ticket from the expanded inbox
  // returns to focus mode.
  const [tabletExpanded, setTabletExpanded] = useState(false)
  // Page-level chrome (pageHead, pageTabs, bulkActions) is hidden when:
  //  - phone mobile-v2: PhoneInboxHeader replaces it
  //  - tablet focus mode: per spec D32 the 40px strip column is the
  //    nav and tabs/filters/bulk-edit are intentionally unavailable.
  // Tablet default + tablet expanded both keep the chrome — that's the
  // only place to switch tabs, search, or bulk-edit at tablet width.
  const inTabletFocus = useMobileV2 && isTablet && !!selectedTicket && !tabletExpanded
  const hideDesktopChrome = (useMobileV2 && isMobile) || inTabletFocus

  // Handle left panel resize
  const handleLeftResize = useCallback((clientX) => {
    if (!mainContainerRef.current) return
    const containerRect = mainContainerRef.current.getBoundingClientRect()
    const newWidth = clientX - containerRect.left
    setLeftPanelWidth(Math.max(250, Math.min(500, newWidth)))
  }, [])

  // Handle right panel resize
  const handleRightResize = useCallback((clientX) => {
    if (!mainContainerRef.current) return
    const containerRect = mainContainerRef.current.getBoundingClientRect()
    const newWidth = containerRect.right - clientX
    setRightPanelWidth(Math.max(200, Math.min(400, newWidth)))
  }, [])

  // Handle quick replies resize
  const handleQuickRepliesResize = useCallback((clientY) => {
    const newHeight = window.innerHeight - clientY - 100 // Account for footer space
    setQuickRepliesHeight(Math.max(80, Math.min(300, newHeight)))
  }, [])

  // Only the newest list request may apply its rows or clear the spinner.
  // An older response landing late (the operator changed tab, or a silent
  // refresh overtook a spinner load) is dropped, so the previous tab's rows
  // never show without the spinner while the newer request is in flight.
  async function loadTickets() {
    const requestNumber = ++listRequest.current
    setLoading(true)
    try {
      const response = await fetchTicketList()
      if (requestNumber === listRequest.current) applyTicketList(response)
    } catch (error) {
      console.error('Failed to load tickets:', error)
    } finally {
      if (requestNumber === listRequest.current) setLoading(false)
    }
  }

  // Same request as loadTickets, no spinner of its own — but it does clear
  // one it overtook, so a superseded spinner load cannot leave it stuck.
  async function refreshTicketsSilently() {
    // ponytail: skipped while extra pages are loaded, so a colleague's activity
    // does not snap the list back to page one; any normal reload still does.
    if (tickets.length > TICKET_PAGE_SIZE) return
    const requestNumber = ++listRequest.current
    try {
      const response = await fetchTicketList()
      if (requestNumber === listRequest.current) applyTicketList(response)
    } catch (error) {
      console.error('Failed to refresh tickets:', error)
    } finally {
      if (requestNumber === listRequest.current) setLoading(false)
    }
  }

  function fetchTicketList(offset = 0) {
    const params = new URLSearchParams({ status: activeTab, limit: String(TICKET_PAGE_SIZE) })
    if (offset) params.set('offset', String(offset))
    if (search) params.set('search', search)
    if (assignmentFilter !== 'all') params.set('assigned_to', assignmentFilter)
    if (topicFilter !== 'all') params.set('topic', topicFilter)
    return api.get(`/api/tickets?${params}`)
  }

  // Next page of the same tab, appended. Dropped if a newer list load started.
  async function loadMoreTickets() {
    const requestNumber = listRequest.current
    setLoadingMoreTickets(true)
    try {
      const response = await fetchTicketList(tickets.length)
      if (requestNumber !== listRequest.current) return
      const next = response.tickets || []
      setTickets(prev => {
        const seen = new Set(prev.map(t => t.id))
        return [...prev, ...next.filter(t => !seen.has(t.id))]
      })
      setHasMoreTickets(next.length === TICKET_PAGE_SIZE)
    } catch (error) {
      console.error('Failed to load more tickets:', error)
    } finally {
      setLoadingMoreTickets(false)
    }
  }

  function applyTicketList(response) {
    setTickets(response.tickets || [])
    setHasMoreTickets((response.tickets || []).length === TICKET_PAGE_SIZE)
    setCounts(response.counts || { new: 0, awaiting_reply: 0, waiting_customer: 0, waiting_third_party: 0, needs_reply: 0, waiting: 0, resolved: 0, spam: 0, service: 0, offers: 0 })
    if (response.lastSyncAt) setLastSyncAt(response.lastSyncAt)
    // Tickets may have been assigned/resolved — tell the nav badge to refresh
    window.dispatchEvent(new Event('tickets:changed'))
  }

  // Re-pull just the thread (GET /:id). Unlike selectTicket this leaves the
  // reply draft, history and notes alone — used after a stale-reply refusal.
  async function reloadThread(ticketId) {
    try {
      setTicketDetails(await api.get(`/api/tickets/${ticketId}`))
    } catch (error) {
      console.error('Failed to reload ticket thread:', error)
    }
  }

  // Helper to determine ticket status indicators
  function getTicketIndicators(ticket) {
    const isNew = lastSyncAt && new Date(ticket.created_at) > new Date(lastSyncAt)
    const isUnviewed = !ticket.viewed_at
    return { isNew, isUnviewed }
  }

  function getUserInitials(userId) {
    const matched = ticketUsers.find(u => (u.user_id || u.id) === userId)
    if (!matched) return '?'
    return (matched.name || '').split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  function getUserName(userId) {
    const matched = ticketUsers.find(u => (u.user_id || u.id) === userId)
    return matched?.name || 'Unknown'
  }

  async function loadTemplates() {
    try {
      const response = await api.get('/api/tickets/templates')
      setTemplates(response.templates || [])
    } catch (error) {
      console.error('Failed to load templates:', error)
    }
  }

  async function loadFilters() {
    try {
      const response = await api.get('/api/tickets/filters')
      setFilters(response.filters || [])
    } catch (error) {
      console.error('Failed to load filters:', error)
    }
  }

  async function loadSyncStatus() {
    try {
      const response = await api.get('/api/tickets/sync-status')
      setSyncStatus(response)
    } catch (error) {
      // Ignore - sync status is optional info
    }
  }

  async function loadTicketUsers() {
    try {
      const response = await api.get('/api/tickets/assignees')
      setTicketUsers(response.users || [])
    } catch (error) {
      console.error('Failed to load ticket users:', error)
    }
  }

  async function loadSyncFailures() {
    setLoadingSyncFailures(true)
    try {
      const response = await api.get('/api/tickets/sync/failures')
      setSyncFailures(response.failures || [])
      setSyncFailureCounts(response.counts || { failed: 0, abandoned: 0, total: 0 })
    } catch (error) {
      console.error('Failed to load sync failures:', error)
    } finally {
      setLoadingSyncFailures(false)
    }
  }

  async function retrySyncFailures(messageIds = null) {
    setRetryingSyncFailures(true)
    try {
      const body = messageIds ? { messageIds } : { retryAll: true }
      const response = await api.post('/api/tickets/sync/retry', body)
      if (response.success) {
        alert(`${response.reset} message(s) queued for retry. Run sync to process them.`)
        loadSyncFailures()
        loadSyncStatus()
      }
    } catch (error) {
      alert('Failed to retry: ' + error.message)
    } finally {
      setRetryingSyncFailures(false)
    }
  }

  async function loadSignature() {
    try {
      const response = await api.get('/api/tickets/signature')
      setSignature(response.signature || '')
    } catch (error) {
      console.error('Failed to load signature:', error)
    }
  }

  async function saveSignature() {
    setSavingSignature(true)
    try {
      await api.post('/api/tickets/signature', { signature })
      setShowSignatureModal(false)
    } catch (error) {
      alert(`Failed to save signature: ${error.message}`)
    } finally {
      setSavingSignature(false)
    }
  }

  async function loadTemplates() {
    setLoadingTemplates(true)
    try {
      const response = await api.get('/api/tickets/templates')
      setTemplates(response.templates || [])
    } catch (error) {
      console.error('Failed to load templates:', error)
    } finally {
      setLoadingTemplates(false)
    }
  }

  async function saveTemplate() {
    if (!templateForm.name || !templateForm.body) {
      alert('Please enter a name and template body')
      return
    }
    setSavingTemplate(true)
    try {
      if (editingTemplate) {
        await api.patch(`/api/tickets/templates/${editingTemplate.id}`, templateForm)
      } else {
        await api.post('/api/tickets/templates', templateForm)
      }
      loadTemplates()
      setEditingTemplate(null)
      setTemplateForm({ name: '', body: '', keywords: '' })
    } catch (error) {
      alert(`Failed to save template: ${error.message}`)
    } finally {
      setSavingTemplate(false)
    }
  }

  async function deleteTemplate(id) {
    if (!confirm('Delete this template?')) return
    try {
      await api.delete(`/api/tickets/templates/${id}`)
      loadTemplates()
    } catch (error) {
      alert(`Failed to delete template: ${error.message}`)
    }
  }

  function editTemplate(template) {
    setEditingTemplate(template)
    setTemplateForm({
      name: template.name,
      body: template.body_text || '',
      keywords: template.keywords ? JSON.parse(template.keywords).join(', ') : ''
    })
  }

  function cancelTemplateEdit() {
    setEditingTemplate(null)
    setTemplateForm({ name: '', body: '', keywords: '' })
  }

  async function syncEmails(fullResync = false) {
    setSyncing(true)
    try {
      // If full re-sync requested, reset the sync state first
      if (fullResync) {
        await api.post('/api/tickets/sync/reset')
      }

      // Process in batches — backend handles all deduplication via ticket_sync_messages table
      let totalSynced = 0
      let totalThreaded = 0
      let totalFiltered = 0
      let totalSkipped = 0
      const allErrors = []
      let batchCount = 0
      let totalEmails = 0
      let totalNew = 0
      let isIncremental = false

      const MAX_BATCHES = 20
      while (true) {
        batchCount++
        if (batchCount > MAX_BATCHES) {
          console.warn(`Reached max batch limit (${MAX_BATCHES}), stopping sync`)
          break
        }
        setSyncStatus(prev => ({ ...prev, syncingBatch: batchCount }))

        const result = await api.post('/api/tickets/sync')

        if (!result.success) {
          throw new Error(result.error || 'Sync failed')
        }

        // Accumulate results
        totalSynced += result.synced || 0
        totalThreaded += result.threaded || 0
        totalFiltered += result.filtered || 0
        if (result.errors?.length) {
          allErrors.push(...result.errors)
        }

        // Capture totals from first batch
        if (batchCount === 1) {
          totalEmails = result.total || 0
          totalNew = result.totalNew || 0
          totalSkipped = result.skipped || 0
          isIncremental = result.isIncremental
        }

        if (!result.hasMore) break

        // Safety: if no successful progress was made in this batch, stop to avoid infinite loop
        const successfulProgress = (result.synced || 0) + (result.threaded || 0) + (result.filtered || 0)
        if (successfulProgress === 0) {
          console.warn('No successful progress in batch (only errors), stopping sync to avoid infinite retry')
          break
        }
      }

      // Build detailed breakdown
      const lines = [`Found ${totalEmails} emails (${isIncremental ? 'incremental' : 'full sync'})`]
      if (batchCount > 1) lines.push(`Processed in ${batchCount} batches`)
      lines.push('')
      lines.push(`✓ ${totalSynced} new tickets created`)
      lines.push(`↩ ${totalThreaded} threaded as replies`)
      lines.push(`⊘ ${totalFiltered} blocked by filters`)
      lines.push(`○ ${totalSkipped} already imported`)

      if (allErrors.length > 0) {
        lines.push(`✗ ${allErrors.length} errors (tracked for auto-retry)`)
        lines.push('')
        lines.push('Failed messages will retry automatically with backoff.')
        lines.push('Check sync failures in the menu for details.')
      }

      alert(lines.join('\n'))
      loadTickets()
      loadSyncStatus()
    } catch (error) {
      alert(`Sync failed: ${error.message}`)
    } finally {
      setSyncing(false)
      setSyncStatus(prev => ({ ...prev, syncingBatch: null }))
    }
  }

  // Open order detail modal from sidebar
  async function openSidebarOrder(order) {
    if (!order.shopifyId) return
    setLoadingSidebarOrder(order.shopifyId)
    try {
      const res = await api.get(`/api/orders/shopify/${order.shopifyId}`)
      if (res.order) {
        setSidebarOrderModal(res.order)
      }
    } catch (err) {
      console.error('Failed to load order:', err)
    } finally {
      setLoadingSidebarOrder(null)
    }
  }

  // Open deal detail modal from sidebar
  function openSidebarDeal(deal) {
    // quote_id + reference_code ride along so the modal's first frame shows
    // the reference code, not the quote UUID, while its refetch is in flight.
    setSidebarDealModal({ id: deal.id, quote_id: deal.quote_id, reference_code: deal.reference_code, dealname: deal.name })
  }

  async function toggleSidebarPin(entityType, entityId) {
    const key = `${entityType}:${entityId}`
    const existingPinId = pinnedItems[key]
    setTogglingPin(key)

    if (existingPinId) {
      // Optimistic unpin
      setPinnedItems(prev => { const next = { ...prev }; delete next[key]; return next })
      try {
        await api.delete(`/api/pinned/${existingPinId}`)
      } catch (err) {
        setPinnedItems(prev => ({ ...prev, [key]: existingPinId }))
        console.error('Failed to unpin:', err)
      }
    } else {
      // Optimistic pin
      setPinnedItems(prev => ({ ...prev, [key]: 'pending' }))
      try {
        const res = await api.post('/api/pinned/quick', { entityType, entityId: String(entityId) })
        setPinnedItems(prev => ({ ...prev, [key]: res.id }))
      } catch (err) {
        setPinnedItems(prev => { const next = { ...prev }; delete next[key]; return next })
        console.error('Failed to pin:', err)
      }
    }
    setTogglingPin(null)
  }

  async function selectTicket(ticket) {
    if (bulkMode) {
      toggleSelection(ticket.id)
      return
    }

    setSelectedTicket(ticket)
    setMobileView('detail') // Legacy <768px mobile view switcher
    setMobileTab('conversation') // mobile-v2 tab — driven on every call,
    // including re-selecting the same ticket, so the user always lands
    // on the conversation view after tapping a row.
    setTabletExpanded(false) // selecting from the expanded inbox returns
    // to focus mode.
    setLoadingDetails(true)
    setReplyText('')
    setReplyFile(null) // Prevent attachment leak — a file picked but
    // never sent on ticket A must not silently end up attached to a
    // reply sent against ticket B.
    setStaleReply(null)
    setComposerOpen(false) // Collapse the composer when switching
    // tickets — the previous ticket's expanded composer is misleading
    // context for the new one (and the textarea is empty anyway).
    setSelectedTemplate(null)
    setCustomerHistory(null)
    setPinnedItems({})
    setNotes([])
    setReturnRequest(null)
    setShowReturnForm(false)

    try {
      const [detailsRes, historyRes, notesRes] = await Promise.all([
        api.get(`/api/tickets/${ticket.id}`),
        api.get(`/api/tickets/${ticket.id}/history`),
        api.get(`/api/tickets/${ticket.id}/notes`)
      ])

      setTicketDetails(detailsRes)
      setCustomerHistory(historyRes)
      setNotes(notesRes.notes || [])

      // Lazy-load Reverb conversation thread for channel='reverb' tickets only.
      // Fires alongside (not blocking) the main ticket fetch — the email body
      // appears immediately, the thread fills in once the API call returns.
      if (detailsRes?.ticket?.channel === 'reverb') {
        setReverbThread(null)
        setReverbThreadStatus('loading')
        setReverbThreadReason(null)
        api.get(`/api/tickets/${ticket.id}/reverb-thread`).then(res => {
          if (res?.thread) {
            setReverbThread(res.thread)
            setReverbThreadFetchedAt(res.fetchedAt || null)
            setReverbThreadStatus('ready')
          } else {
            setReverbThreadStatus('unavailable')
            setReverbThreadReason(res?.reason || 'no thread available')
          }
        }).catch(err => {
          console.error('Failed to load Reverb thread:', err)
          setReverbThreadStatus('error')
          setReverbThreadReason(err.message || 'request failed')
        })
      } else {
        setReverbThread(null)
        setReverbThreadStatus('idle')
      }

      // Build pin status map from history response
      const pinMap = {}
      if (historyRes.deals) {
        historyRes.deals.forEach(d => { if (d.pinned) pinMap[`deal:${d.id}`] = d.pinId })
      }
      if (historyRes.orders) {
        historyRes.orders.forEach(o => { if (o.pinned) pinMap[`order:${o.shopifyId || o.id}`] = o.pinId })
      }
      if (historyRes.previousTickets) {
        historyRes.previousTickets.forEach(t => { if (t.pinned) pinMap[`ticket:${t.id}`] = t.pinId })
      }
      setPinnedItems(pinMap)

      // Always check for existing case on this ticket
      api.get(`/api/orders/returns/by-ticket/${ticket.id}`).then(res => {
        setReturnRequest(res.returnRequest || null)
      }).catch(() => {})

      // Mark as read in Gmail when viewing
      if (ticket.status === 'new' || ticket.status === 'awaiting_reply' || !ticket.read_at) {
        api.post(`/api/tickets/${ticket.id}/mark-read`).catch(err => {
          console.error('Failed to mark ticket as read in Gmail:', err)
        })
      }
    } catch (error) {
      console.error('Failed to load ticket details:', error)
    } finally {
      setLoadingDetails(false)
    }
  }

  const [aiLoading, setAiLoading] = useState(false)
  const [rewriteLoading, setRewriteLoading] = useState(false)
  const [showRewriteModal, setShowRewriteModal] = useState(false)
  const [rewrittenText, setRewrittenText] = useState('')
  const [rewriteReported, setRewriteReported] = useState(false)
  const [rewriteContext, setRewriteContext] = useState('auto') // 'auto', 'trade_in', 'order'
  const [suggestTemplate, setSuggestTemplate] = useState('auto') // 'auto', 'market_price', 'counter_offer', 'fair_price'
  // Counter offer on a customer with several quotes: the server sends the
  // quotes back and staff pick one, which re-runs Suggest for that quote.
  const [quoteChoices, setQuoteChoices] = useState(null)
  useEffect(() => setQuoteChoices(null), [selectedTicket?.id, suggestTemplate])

  // Called bare from the buttons; with a quote id from the picker.
  async function getSuggestion(dealId) {
    if (!selectedTicket) return

    setAiLoading(true)
    setQuoteChoices(null)
    try {
      const body = { template: suggestTemplate, ...(typeof dealId === 'string' ? { dealId } : {}) }
      const response = await api.post(`/api/tickets/${selectedTicket.id}/suggest`, body)
      if (response.quoteChoices?.length) {
        setQuoteChoices(response.quoteChoices)
      } else if (response.suggestedResponse) {
        setReplyText(response.suggestedResponse)
        if (response.template) {
          setSelectedTemplate(response.template.key)
        }
      } else {
        alert(response.message || 'Could not generate AI suggestion.')
      }
    } catch (error) {
      alert(`Failed to get suggestion: ${error.message}`)
    } finally {
      setAiLoading(false)
    }
  }

  async function getRewrite(contextOverride = null) {
    if (!selectedTicket || !replyText.trim()) {
      alert('Please enter some text to rewrite.')
      return
    }

    setRewriteLoading(true)
    try {
      const response = await api.post(`/api/tickets/${selectedTicket.id}/rewrite`, {
        text: replyText,
        contextType: contextOverride || rewriteContext
      })
      if (response.rewrittenText) {
        setRewrittenText(response.rewrittenText)
        setShowRewriteModal(true)
      } else {
        alert(response.message || 'Could not rewrite text.')
      }
    } catch (error) {
      alert(`Failed to rewrite: ${error.message}`)
    } finally {
      setRewriteLoading(false)
    }
  }

  function acceptRewrite() {
    setReplyText(rewrittenText)
    setShowRewriteModal(false)
    setRewrittenText('')
  }

  function cancelRewrite() {
    setShowRewriteModal(false)
    setRewrittenText('')
  }

  async function reportBadRewrite() {
    try {
      await api.post(`/api/tickets/${selectedTicket.id}/rewrite-feedback`, {
        originalDraft: replyText,
        rewrittenText,
        threadContext: null
      })
      setRewriteReported(true)
      setTimeout(() => {
        setShowRewriteModal(false)
        setRewrittenText('')
        setRewriteReported(false)
      }, 1000)
    } catch (error) {
      alert(`Failed to report: ${error.message}`)
    }
  }

  function useTemplate(template) {
    let text = template.body_text
    text = text.replace(/\{\{customer_name\}\}/g, selectedTicket?.from_name || 'Customer')
    setReplyText(text)
    setSelectedTemplate(template.key)
  }

  // Returns `true` when the reply was successfully accepted by the
  // backend, `false` otherwise. Phone composer relies on this to decide
  // whether to apply a follow-up status change — pre-fix, a failed
  // send + a "Mark as: Resolved" picker would resolve the ticket even
  // though no reply went out.
  async function sendReply() {
    if (!replyText.trim() || !selectedTicket) return false

    setSending(true)
    try {
      const formData = new FormData()
      formData.append('message', replyText)
      if (selectedTemplate) formData.append('templateKey', selectedTemplate)
      if (replyFile) formData.append('file', replyFile)
      // What this view believes the ticket's last activity is. The server
      // refuses (409 stale_reply) if the ticket moved on since — a
      // colleague answered, or the customer wrote again.
      formData.append('expected_last_message_at', ticketDetails?.ticket?.last_message_at ?? '')

      const res = await fetch(`/api/tickets/${selectedTicket.id}/reply`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      })
      const data = await res.json().catch(() => ({}))
      if (res.status === 409 && data.error === 'stale_reply') {
        // Keep the draft; show who got there first; reload the thread so
        // the next send carries the current last_message_at.
        setStaleReply(data)
        await reloadThread(selectedTicket.id)
        refreshTicketsSilently()
        return false
      }
      if (!res.ok) {
        throw new Error(data.error || 'Failed to send reply')
      }
      // Warn if marketplace send failed (eBay/Reverb)
      if ((data.channel === 'ebay' || data.channel === 'reverb') && !data.marketplaceSent) {
        const channelName = data.channel === 'ebay' ? 'eBay' : 'Reverb'
        alert(`Warning: Reply saved locally but ${channelName} send failed. Please send manually via ${channelName}.`)
      } else {
        alert(data.message || 'Reply sent successfully!')
      }
      setReplyText('')
      setSelectedTemplate(null)
      setReplyFile(null)
      loadTickets()
      selectTicket(selectedTicket)
      return true
    } catch (error) {
      alert(`Failed to send: ${error.message}`)
      return false
    } finally {
      setSending(false)
    }
  }

  async function handleAssign(ticketId, userId) {
    try {
      await api.patch(`/api/tickets/${ticketId}`, { assigned_to: userId })
      // Patch state in place. This used to call loadTickets() + selectTicket(),
      // which re-fetched and remounted both panels — so every assignment threw
      // away the operator's scroll position in the email list. Assignment
      // changes ONE field; the only structural consequence is a row that no
      // longer matches the active assignment filter, which is dropped (the
      // list stays mounted, so scroll survives).
      const keepsRow = assignmentFilter === 'all'
        || (assignmentFilter === 'me' && userId === user?.user_id)
        || (assignmentFilter === 'unassigned' && userId == null)
      setTickets(prev => keepsRow
        ? prev.map(t => t.id === ticketId ? { ...t, assigned_to: userId } : t)
        : prev.filter(t => t.id !== ticketId))
      setTicketDetails(prev => prev?.ticket?.id === ticketId
        ? { ...prev, ticket: { ...prev.ticket, assigned_to: userId } }
        : prev)
      // loadTickets() used to fire this for the nav badge; keep it firing.
      window.dispatchEvent(new Event('tickets:changed'))
    } catch (error) {
      console.error('Failed to assign ticket:', error)
      alert(`Failed to assign: ${error.message}`)
    }
  }

  // Same in-place patch as handleAssign: one field changes, so the list stays
  // mounted and keeps its scroll. A row that leaves the topic filter is dropped.
  async function handleTopicChange(ticketId, topic) {
    try {
      await api.put(`/api/tickets/${ticketId}/topic`, { topic })
      const keepsRow = topicFilter === 'all' || topicFilter === topic
      setTickets(prev => keepsRow
        ? prev.map(t => t.id === ticketId ? { ...t, topic, topic_source: 'staff' } : t)
        : prev.filter(t => t.id !== ticketId))
      setTicketDetails(prev => prev?.ticket?.id === ticketId
        ? { ...prev, ticket: { ...prev.ticket, topic, topic_source: 'staff' } }
        : prev)
    } catch (error) {
      console.error('Failed to set ticket topic:', error)
      alert(`Failed to set the topic: ${error.message}`)
    }
  }

  async function updateTicketStatus(ticketId, status) {
    try {
      await api.patch(`/api/tickets/${ticketId}`, { status })
      loadTickets()
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(null)
        setTicketDetails(null)
      }
    } catch (error) {
      alert(`Failed to update: ${error.message}`)
    }
  }

  // Bulk actions
  function toggleSelection(id) {
    const newSet = new Set(selectedIds)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setSelectedIds(newSet)
  }

  function selectAll() {
    if (selectedIds.size === tickets.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(tickets.map(t => t.id)))
    }
  }

  async function bulkUpdateStatus(status) {
    if (selectedIds.size === 0) return
    if (!confirm(`Mark ${selectedIds.size} ticket(s) as ${status}?`)) return

    try {
      await api.post('/api/tickets/bulk-status', {
        ticketIds: Array.from(selectedIds),
        status
      })
      setSelectedIds(new Set())
      setBulkMode(false)
      loadTickets()
    } catch (error) {
      alert(`Failed: ${error.message}`)
    }
  }

  // Extract a readable email + domain from a "Name <email>" or plain from_email
  function senderTargets(fromEmail) {
    const email = (fromEmail || '').match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/)?.[0]?.toLowerCase() || ''
    const domain = email.includes('@') ? email.split('@')[1] : ''
    return { email, domain }
  }

  // Quick ignore sender
  async function ignoreSender(type = 'email') {
    if (!selectedTicket) return
    const { email, domain } = senderTargets(selectedTicket.from_email)
    const warning = type === 'domain'
      ? `⚠️ Block EVERY email from the domain "${domain}"?\n\nThis filters out ALL current and future senders at this domain — not just this person. To block only this sender, cancel and use "Block email" instead.`
      : `Block all future emails from "${email || selectedTicket.from_email}"?`
    if (!confirm(warning)) return

    try {
      const result = await api.post(`/api/tickets/${selectedTicket.id}/ignore-sender`, { type })
      if (result.alreadyExists) {
        alert(`Filter already exists for: ${result.pattern}`)
      } else {
        alert(`Filter added for: ${result.pattern}`)
      }
      setSelectedTicket(null)
      setTicketDetails(null)
      loadTickets()
    } catch (error) {
      alert(`Failed: ${error.message}`)
    }
  }

  // Mark sender/domain as Service Email
  async function markAsService(type = 'email') {
    if (!selectedTicket) return
    const { email, domain } = senderTargets(selectedTicket.from_email)
    const warning = type === 'domain'
      ? `⚠️ Route EVERY email from the domain "${domain}" to Service?\n\nThis moves ALL senders at this domain out of the main inbox — not just this person. To route only this sender, cancel and use "Mark sender as Service (email)".`
      : `Mark all future emails from "${email || selectedTicket.from_email}" as Service Emails?`
    if (!confirm(warning)) return

    try {
      const result = await api.post(`/api/tickets/${selectedTicket.id}/set-service`, { type })
      alert(`Service filter added for: ${result.pattern}`)
      // Also update the current ticket to service status
      await updateTicketStatus(selectedTicket.id, 'service')
      setSelectedTicket(null)
      setTicketDetails(null)
      loadTickets()
    } catch (error) {
      alert(`Failed: ${error.message}`)
    }
  }

  async function pinTicket() {
    if (!selectedTicket || !pinData.reason) return

    try {
      const body = {
        entityType: 'ticket',
        entityId: String(selectedTicket.id).replace('.0', ''),
        issueArea: 'Ticket',
        reason: pinData.reason,
        dueDate: pinData.dueDate || null
      }
      if (pinData.assignedTo) {
        body.assignedTo = Number(pinData.assignedTo)
      }
      const res = await api.post('/api/pinned', body)
      setPinnedItems(prev => ({ ...prev, [`ticket:${selectedTicket.id}`]: res.id || 'pinned' }))
      setShowPinModal(false)
      setPinData({ reason: '', dueDate: '', assignedTo: '' })
    } catch (error) {
      alert(`Failed to pin: ${error.message}`)
    }
  }

  // Notes
  async function addNote() {
    if (!newNote.trim() || !selectedTicket) return

    setSavingNote(true)
    try {
      await api.post(`/api/tickets/${selectedTicket.id}/notes`, { content: newNote })
      setNewNote('')
      const notesRes = await api.get(`/api/tickets/${selectedTicket.id}/notes`)
      setNotes(notesRes.notes || [])
    } catch (error) {
      alert(`Failed to add note: ${error.message}`)
    } finally {
      setSavingNote(false)
    }
  }

  // Case handlers
  // The order the case is for: the one chosen in the form, else the default.
  function findChannelOrder() {
    return resolveCaseOrder(customerHistory?.orders, selectedTicket?.channel, caseOrderId)
  }

  // A new ticket or a closed form starts again from the default order.
  useEffect(() => { setCaseOrderId(null) }, [showReturnForm, selectedTicket?.id])

  // Load the order's lines when the case form opens. A single line ticks itself.
  useEffect(() => {
    if (!showReturnForm || !selectedTicket) { setCaseLines(null); setCaseLineRefs([]); return }
    const order = findChannelOrder()
    setCaseLineRefs([])
    if (!order) { setCaseLines([]); return }
    let alive = true
    const params = new URLSearchParams()
    if (order.source_name) params.set('source_name', order.source_name)
    if (order.id) params.set('order_name', order.id)
    if (selectedTicket.channel) params.set('ticket_channel', selectedTicket.channel)
    if (order.shopifyId) params.set('shopify_order_id', order.shopifyId)
    if (order.reverb_order_id) params.set('reverb_order_id', order.reverb_order_id)
    api.get(`/api/orders/returns/order-lines?${params}`)
      .then(res => {
        if (!alive) return
        const lines = (res.line_items || []).filter(l => l.selectable)
        setCaseLines(lines)
        setCaseLineRefs(lines.length === 1 ? [lines[0].ref] : [])
      })
      .catch(() => { if (alive) setCaseLines([]) })
    return () => { alive = false }
  }, [showReturnForm, selectedTicket?.id, caseOrderId])

  async function createReturnRequest() {
    if (!selectedTicket || !returnFormData.reason_id) return
    setReturnLoading(true)
    try {
      const order = findChannelOrder()
      const tickedLines = (caseLines || []).filter(l => caseLineRefs.includes(l.ref))
      const product = caseProduct(order, tickedLines)
      const items = tickedLines
        .map(l => ({ ref: l.ref, title: l.title, sku: l.sku, quantity: l.quantity, price: l.value }))
      const createRes = await api.post('/api/orders/returns', {
        ticket_id: selectedTicket.id,
        reason_id: parseInt(returnFormData.reason_id),
        reason_notes: returnFormData.reason_notes || null,
        customer_description: ticketDetails?.ticket?.body_text || null,
        order_id: order?.shopifyId || null,
        order_name: order?.id || null,
        shopify_order_id: order?.shopifyId || null,
        product_title: product.title,
        product_type: product.type,
        order_date: order?.created || order?.createdAt || null,
        override_30_day: override30DayCase || false,
        source_name: order?.source_name || null,
        channel_conversation_id: order?.channel_conversation_id || null,
        reverb_order_id: order?.reverb_order_id || null,
        items
      })
      // Case-opened notification email is sent by the backend directly from no-reply@audiotrader.co.uk
      const returnRes = await api.get(`/api/orders/returns/by-ticket/${selectedTicket.id}`)
      setReturnRequest(returnRes.returnRequest || null)
      setShowReturnForm(false)
      setReturnFormData({ reason_id: '', reason_notes: '' })
    } catch (error) {
      alert(`Failed to open case: ${error.message}`)
    } finally {
      setReturnLoading(false)
    }
  }

  function getCaseStatusBadge(status) {
    const styles = {
      logged: { bg: '#dbeafe', color: '#1e40af', label: 'Open' },
      awaiting_evidence: { bg: '#fef3c7', color: '#92400e', label: 'Awaiting Evidence' },
      troubleshooting_sent: { bg: '#e0e7ff', color: '#3730a3', label: 'Troubleshooting' },
      return_approved: { bg: '#d1fae5', color: '#065f46', label: 'Return Approved' },
      refunded: { bg: '#d1fae5', color: '#065f46', label: 'Refunded' },
      closed: { bg: '#f3f4f6', color: '#6b7280', label: 'Closed' }
    }
    const s = styles[status] || { bg: '#f3f4f6', color: '#6b7280', label: status }
    return (
      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: s.bg, color: s.color }}>
        {s.label}
      </span>
    )
  }

  // Filters
  async function addFilter() {
    if (!newFilter.pattern) return

    try {
      const filterData = { ...newFilter }
      // Only include subject fields if they're set
      if (!filterData.subject_pattern) {
        delete filterData.subject_pattern
        delete filterData.subject_match_type
      }
      await api.post('/api/tickets/filters', filterData)
      setNewFilter({ filter_type: 'email', pattern: '', match_type: 'exact', action: 'block', reason: '', subject_pattern: '', subject_match_type: 'contains' })
      setShowAdvanced(false)
      loadFilters()
    } catch (error) {
      alert(`Failed to add filter: ${error.message}`)
    }
  }

  async function deleteFilter(id) {
    if (!confirm('Delete this filter?')) return

    try {
      await api.delete(`/api/tickets/filters/${id}`)
      loadFilters()
    } catch (error) {
      alert(`Failed to delete: ${error.message}`)
    }
  }

  async function applyFilterToExisting(id) {
    if (!confirm('Apply this filter to all existing tickets that match? This will update their status or delete them based on the filter action.')) return

    try {
      const result = await api.post(`/api/tickets/filters/${id}/apply`)
      if (result.deleted) {
        alert(`Deleted ${result.deleted} matching ticket(s)`)
      } else if (result.updated) {
        alert(`Updated ${result.updated} ticket(s) to ${result.newStatus}`)
      } else {
        alert(result.message || 'No matching tickets found')
      }
      loadTickets()
    } catch (error) {
      alert(`Failed to apply filter: ${error.message}`)
    }
  }

  // Group templates by category
  const templatesByCategory = templates.reduce((acc, t) => {
    const cat = t.category || 'general'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(t)
    return acc
  }, {})

  // Priority badge colors
  const priorityColors = {
    urgent: '#dc2626',
    high: '#ea580c',
    normal: '#6b7280',
    low: '#22c55e'
  }

  // Helper: derive ticket type from template_category
  function getTicketType(ticket) {
    const cat = ticket.template_category
    if (!cat) return null
    const labels = {
      'returns': 'Return',
      'shipping': 'Shipping',
      'order': 'Order',
      'trade_in': 'Trade-In',
      'general': 'General',
      'payment': 'Payment',
      'product': 'Product',
      'account': 'Account'
    }
    return labels[cat] || cat.charAt(0).toUpperCase() + cat.slice(1)
  }

  // Helper: map status to display label + CSS class
  function getStateDisplay(status) {
    const map = {
      'new': { label: 'New', className: 'ticket-row__state--new' },
      'awaiting_reply': { label: 'Awaiting reply', className: 'ticket-row__state--awaiting-reply' },
      'waiting_customer': { label: 'Waiting on customer', className: 'ticket-row__state--waiting-customer' },
      'waiting_third_party': { label: 'Waiting on 3rd party', className: 'ticket-row__state--waiting-third-party' },
      'resolved': { label: 'Resolved', className: 'ticket-row__state--resolved' },
      'spam': { label: 'Spam', className: '' },
      'service': { label: 'Service', className: '' },
      'offers': { label: 'Offer', className: '' }
    }
    return map[status] || { label: status, className: '' }
  }

  // View-component nodes — built once per render and handed to
  // DesktopTicketsLayout (and, in Phases 3+4, to the mobile/tablet
  // layouts) so each layout owns spatial composition only.
  const inboxNode = (
    <TicketsInbox
      breakpoint={breakpoint}
      mobileV2={useMobileV2}
      isMobile={isMobile}
      mobileView={mobileView}
      selectedTicket={selectedTicket}
      leftPanelWidth={leftPanelWidth}
      tickets={tickets}
      loading={loading}
      hasMore={hasMoreTickets}
      loadingMore={loadingMoreTickets}
      onLoadMore={loadMoreTickets}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      counts={counts}
      search={search}
      setSearch={setSearch}
      bulkMode={bulkMode}
      setBulkMode={setBulkMode}
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      selectAll={selectAll}
      toggleSelection={toggleSelection}
      bulkUpdateStatus={bulkUpdateStatus}
      getStateDisplay={getStateDisplay}
      getTicketType={getTicketType}
      getUserName={getUserName}
      getUserInitials={getUserInitials}
      selectTicket={selectTicket}
      setCustomerEmail={setCustomerEmail}
      user={user}
      handleAssign={handleAssign}
      viewersByTicket={viewersByTicket}
      onOpenFilters={() => { /* PhoneFilterSheet owns its own open state */ }}
      onSync={() => syncEmails(false)}
      onOpenEmailFilters={() => { setShowFilterModal(true); setFilterSearch(''); loadFilters(); loadTicketUsers() }}
      onOpenSignature={() => { loadSignature(); setShowSignatureModal(true) }}
    />
  )

  const conversationNode = (
    <TicketConversation
      breakpoint={breakpoint}
      mobileV2={useMobileV2}
      composerOpen={composerOpen}
      setComposerOpen={setComposerOpen}
      onBackToInbox={() => { setSelectedTicket(null); setTicketDetails(null); setMobileTab('inbox') }}
      isMobile={isMobile}
      mobileView={mobileView}
      setMobileView={setMobileView}
      selectedTicket={selectedTicket}
      ticketDetails={ticketDetails}
      loadingDetails={loadingDetails}
      viewers={selectedTicket ? viewersByTicket[selectedTicket.id] : undefined}
      staleReply={staleReply}
      clearStaleReply={() => setStaleReply(null)}
      reverbThread={reverbThread}
      reverbThreadStatus={reverbThreadStatus}
      reverbThreadReason={reverbThreadReason}
      reverbThreadFetchedAt={reverbThreadFetchedAt}
      reverbThreadRefreshing={reverbThreadRefreshing}
      setReverbThread={setReverbThread}
      setReverbThreadStatus={setReverbThreadStatus}
      setReverbThreadReason={setReverbThreadReason}
      setReverbThreadFetchedAt={setReverbThreadFetchedAt}
      setReverbThreadRefreshing={setReverbThreadRefreshing}
      replyText={replyText}
      setReplyText={setReplyText}
      sending={sending}
      replyFile={replyFile}
      setReplyFile={setReplyFile}
      aiLoading={aiLoading}
      rewriteLoading={rewriteLoading}
      rewriteContext={rewriteContext}
      setRewriteContext={setRewriteContext}
      suggestTemplate={suggestTemplate}
      setSuggestTemplate={setSuggestTemplate}
      quoteChoices={quoteChoices}
      setShowPinModal={setShowPinModal}
      setPinData={setPinData}
      setPinUsers={setPinUsers}
      user={user}
      ticketUsers={ticketUsers}
      getUserName={getUserName}
      handleAssign={handleAssign}
      handleTopicChange={handleTopicChange}
      getSuggestion={getSuggestion}
      getRewrite={getRewrite}
      updateTicketStatus={updateTicketStatus}
      sendReply={sendReply}
      markAsService={markAsService}
      ignoreSender={ignoreSender}
    />
  )

  const contextNode = (
    <TicketContext
      breakpoint={breakpoint}
      mobileV2={useMobileV2}
      isMobile={isMobile}
      mobileView={mobileView}
      rightPanelWidth={rightPanelWidth}
      selectedTicket={selectedTicket}
      ticketDetails={ticketDetails}
      customerHistory={customerHistory}
      pinnedItems={pinnedItems}
      togglingPin={togglingPin}
      loadingHistory={loadingHistory}
      loadingSidebarOrder={loadingSidebarOrder}
      notes={notes}
      newNote={newNote}
      setNewNote={setNewNote}
      savingNote={savingNote}
      returnRequest={returnRequest}
      returnReasons={returnReasons}
      showReturnForm={showReturnForm}
      setShowReturnForm={setShowReturnForm}
      returnFormData={returnFormData}
      setReturnFormData={setReturnFormData}
      returnLoading={returnLoading}
      override30DayCase={override30DayCase}
      setOverride30DayCase={setOverride30DayCase}
      caseLines={caseLines}
      caseLineRefs={caseLineRefs}
      setCaseOrderId={setCaseOrderId}
      setCaseLineRefs={setCaseLineRefs}
      setCustomerEmail={setCustomerEmail}
      openSidebarDeal={openSidebarDeal}
      openSidebarOrder={openSidebarOrder}
      toggleSidebarPin={toggleSidebarPin}
      selectTicket={selectTicket}
      getStateDisplay={getStateDisplay}
      getCaseStatusBadge={getCaseStatusBadge}
      findChannelOrder={findChannelOrder}
      createReturnRequest={createReturnRequest}
      addNote={addNote}
    />
  )

  return (
    <>
    <div className="page page--tickets">
      {/* Desktop chrome — search, sync, tab strip, bulk actions, legacy
          mobile view toggle. Hidden on phone mobile-v2 (PhoneInboxHeader
          replaces it) and in tablet focus mode (spec D32). Tablet default
          and tablet expanded both keep the chrome. */}
      {!hideDesktopChrome && (
      <>
      <div className="pageHead">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'flex-start',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 12 : 0
        }}>
          <div>
            <h1>Tickets</h1>
            {!isMobile && <p>Customer support inbox with AI-powered responses</p>}
          </div>
          <div style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'flex-start' : 'flex-end'
          }}>
            {syncStatus?.lastSyncAt && !isMobile && (
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                Last sync: {formatDateTime(syncStatus.lastSyncAt)}
                {syncStatus.hasHistoryId && ' (incremental)'}
              </span>
            )}
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <button
                className={`btn ${isMobile ? 'btn-sm' : ''} btn-primary`}
                onClick={() => syncEmails(false)}
                disabled={syncing}
              >
                {syncing ? (syncStatus?.syncingBatch > 1 ? `Syncing (batch ${syncStatus.syncingBatch})...` : 'Syncing...') : 'Sync'}
              </button>
              {(syncStatus?.failedMessages > 0 || syncStatus?.abandonedMessages > 0) && (
                <span
                  onClick={(e) => { e.stopPropagation(); loadSyncFailures(); setShowSyncFailuresModal(true); }}
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    background: '#DC2626',
                    color: 'white',
                    borderRadius: '50%',
                    width: 18,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    lineHeight: 1
                  }}
                  title="Sync failures - click to view"
                >
                  {(syncStatus.failedMessages || 0) + (syncStatus.abandonedMessages || 0)}
                </span>
              )}
            </div>
            {/* Menu dropdown */}
            <div ref={menuDropdownRef} style={{ position: 'relative' }}>
              <button
                className={`btn ${isMobile ? 'btn-sm' : ''} btn-secondary`}
                onClick={() => setShowMenuDropdown(!showMenuDropdown)}
                title="More options"
                style={{ padding: '8px 10px' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
              {showMenuDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 4,
                  background: 'white',
                  border: '1px solid var(--stroke)',
                  borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  minWidth: 160,
                  zIndex: 100,
                  overflow: 'hidden'
                }}>
                  <button
                    onClick={() => { setShowFilterModal(true); setFilterSearch(''); loadFilters(); loadTicketUsers(); setShowMenuDropdown(false); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '10px 14px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: 14,
                      textAlign: 'left'
                    }}
                    onMouseEnter={e => e.target.style.background = '#f3f4f6'}
                    onMouseLeave={e => e.target.style.background = 'none'}
                  >
                    🔍 Filters
                  </button>
                  <button
                    onClick={() => { loadSignature(); setShowSignatureModal(true); setShowMenuDropdown(false); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '10px 14px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: 14,
                      textAlign: 'left'
                    }}
                    onMouseEnter={e => e.target.style.background = '#f3f4f6'}
                    onMouseLeave={e => e.target.style.background = 'none'}
                  >
                    ✍️ Signature
                  </button>
                  <button
                    onClick={() => { loadTemplates(); setShowTemplateModal(true); setShowMenuDropdown(false); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '10px 14px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: 14,
                      textAlign: 'left'
                    }}
                    onMouseEnter={e => e.target.style.background = '#f3f4f6'}
                    onMouseLeave={e => e.target.style.background = 'none'}
                  >
                    📝 Templates
                  </button>
                  <button
                    onClick={() => { loadSyncFailures(); setShowSyncFailuresModal(true); setShowMenuDropdown(false); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '10px 14px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: 14,
                      textAlign: 'left'
                    }}
                    onMouseEnter={e => e.target.style.background = '#f3f4f6'}
                    onMouseLeave={e => e.target.style.background = 'none'}
                  >
                    ⚠️ Sync Failures
                    {(syncStatus?.failedMessages > 0 || syncStatus?.abandonedMessages > 0) && (
                      <span style={{
                        background: '#DC2626',
                        color: 'white',
                        borderRadius: 10,
                        padding: '1px 6px',
                        fontSize: 11,
                        fontWeight: 600,
                        marginLeft: 'auto'
                      }}>
                        {(syncStatus.failedMessages || 0) + (syncStatus.abandonedMessages || 0)}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => { syncEmails(true); setShowMenuDropdown(false); }}
                    disabled={syncing}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '10px 14px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: 14,
                      textAlign: 'left',
                      color: '#DC2626'
                    }}
                    onMouseEnter={e => e.target.style.background = '#fef2f2'}
                    onMouseLeave={e => e.target.style.background = 'none'}
                  >
                    🔄 Full Re-Sync
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          className="form-input"
          placeholder={isMobile ? "Search..." : "Search by email, subject, or name..."}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: isMobile ? '100%' : 400, width: '100%', flex: '1 1 auto' }}
        />
      </div>

      {/* Assignment Filter + Status Tabs */}
      <div style={{ marginBottom: 6 }}>
        <select
          className="form-input"
          value={assignmentFilter}
          onChange={e => setAssignmentFilter(e.target.value)}
          style={{ width: 'auto', minWidth: 150, fontSize: 13, padding: '4px 8px' }}
          aria-label="Filter tickets by assignment"
        >
          <option value="all">All Tickets</option>
          <option value="me">My Tickets</option>
          <option value="unassigned">Unassigned</option>
        </select>
        <select
          className="form-input"
          value={topicFilter}
          onChange={e => setTopicFilter(e.target.value)}
          style={{ width: 'auto', minWidth: 150, fontSize: 13, padding: '4px 8px', marginLeft: 8 }}
          aria-label="Filter tickets by topic"
        >
          <option value="all">All topics</option>
          {TICKET_TOPICS.map(topic => <option key={topic.key} value={topic.key}>{topic.label}</option>)}
          <option value="none">No topic yet</option>
        </select>
        <button className="btn btn-secondary btn-sm" style={{ marginLeft: 8 }} onClick={() => setShowTopicCounts(true)}>
          Topics this week
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div
          className="pageTabs"
          style={{
            overflowX: isMobile ? 'auto' : 'visible',
            whiteSpace: isMobile ? 'nowrap' : 'normal',
            paddingBottom: isMobile ? 8 : 0,
            WebkitOverflowScrolling: 'touch',
            flex: 1
          }}
        >
          <button
            className={activeTab === 'needs_reply' ? 'active' : ''}
            onClick={() => setActiveTab('needs_reply')}
            style={{ fontSize: isMobile ? 12 : undefined, position: 'relative' }}
          >
            {(counts.needs_reply || 0) > 0 && <span style={{ display: 'inline-block', width: 8, height: 8, background: '#dc2626', borderRadius: '50%', marginRight: 6 }} />}
            Needs Reply ({counts.needs_reply || 0})
          </button>
          <button
            className={activeTab === 'offers' ? 'active' : ''}
            onClick={() => setActiveTab('offers')}
            style={{ fontSize: isMobile ? 12 : undefined }}
          >
            Offers ({counts.offers || 0})
          </button>
          {/* More dropdown for secondary tabs */}
          <div ref={moreDropdownRef} className="ticket-tabs__more" style={{ display: 'inline-block' }}>
            <button
              className={['waiting', 'resolved', 'service', 'spam'].includes(activeTab) ? 'active' : ''}
              onClick={() => setShowMoreDropdown(!showMoreDropdown)}
              style={{ fontSize: isMobile ? 12 : undefined }}
            >
              {activeTab === 'waiting' ? `Waiting (${counts.waiting || 0})` :
               activeTab === 'resolved' ? `Resolved (${counts.resolved || 0})` :
               activeTab === 'service' ? `Service (${counts.service || 0})` :
               activeTab === 'spam' ? `Spam (${counts.spam || 0})` :
               'More'}
              <span style={{ marginLeft: 4, fontSize: 10 }}>&#9662;</span>
            </button>
            {showMoreDropdown && (
              <div className="ticket-tabs__dropdown">
                <button
                  className={`ticket-tabs__dropdown-item ${activeTab === 'waiting' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('waiting'); setShowMoreDropdown(false); }}
                >
                  Waiting <span className="ticket-tabs__dropdown-count">{counts.waiting || 0}</span>
                </button>
                <button
                  className={`ticket-tabs__dropdown-item ${activeTab === 'resolved' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('resolved'); setShowMoreDropdown(false); }}
                >
                  Resolved <span className="ticket-tabs__dropdown-count">{counts.resolved || 0}</span>
                </button>
                <button
                  className={`ticket-tabs__dropdown-item ${activeTab === 'service' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('service'); setShowMoreDropdown(false); }}
                >
                  Service <span className="ticket-tabs__dropdown-count">{counts.service || 0}</span>
                </button>
                <button
                  className={`ticket-tabs__dropdown-item ${activeTab === 'spam' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('spam'); setShowMoreDropdown(false); }}
                >
                  Spam <span className="ticket-tabs__dropdown-count">{counts.spam || 0}</span>
                </button>
              </div>
            )}
          </div>
        </div>
        <button
          className={`btn ${isMobile ? 'btn-sm' : ''} ${bulkMode ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setBulkMode(!bulkMode); setSelectedIds(new Set()); }}
          style={{ flexShrink: 0 }}
        >
          {bulkMode ? 'Cancel Bulk' : 'Bulk Edit'}
        </button>
      </div>

      {/* Bulk Actions Bar */}
      {bulkMode && selectedIds.size > 0 && (
        <div style={{
          display: 'flex',
          gap: 12,
          padding: 12,
          background: 'var(--bg)',
          borderRadius: 8,
          marginTop: 16,
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: 600 }}>{selectedIds.size} selected</span>
          <button className="btn btn-sm btn-success" onClick={() => bulkUpdateStatus('resolved')}>
            Mark Resolved
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => bulkUpdateStatus('waiting_customer')}>
            Waiting on Customer
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => bulkUpdateStatus('waiting_third_party')}>
            Waiting on 3rd Party
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => bulkUpdateStatus('service')}>
            Mark Service
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => bulkUpdateStatus('spam')}>
            Mark Spam
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => bulkUpdateStatus('new')}>
            Reopen
          </button>
        </div>
      )}

      {/* Mobile View Toggle */}
      {isMobile && selectedTicket && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, background: 'var(--bg)', padding: 8, borderRadius: 8 }}>
          <button
            className={`btn btn-sm ${mobileView === 'list' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setMobileView('list')}
          >
            List
          </button>
          <button
            className={`btn btn-sm ${mobileView === 'detail' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setMobileView('detail')}
          >
            Detail
          </button>
          <button
            className={`btn btn-sm ${mobileView === 'sidebar' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setMobileView('sidebar')}
          >
            Info
          </button>
        </div>
      )}
      </>
      )}

      {/* Main Content — layout selection:
            phone + mobile-v2  → MobileTicketsLayout (bottom tabs)
            tablet + mobile-v2 → TabletTicketsLayout (default 2-col / focus mode)
            else               → DesktopTicketsLayout (3-col)               */}
      {useMobileV2 && isMobile ? (
        <MobileTicketsLayout
          inbox={inboxNode}
          conversation={conversationNode}
          context={contextNode}
          hasSelectedTicket={!!selectedTicket}
          tab={mobileTab}
          setTab={setMobileTab}
          hideTabs={bulkMode || composerOpen /* hide bottom tabs when bulk-edit OR composer takes the screen */}
        />
      ) : useMobileV2 && isTablet ? (
        <TabletTicketsLayout
          inbox={inboxNode}
          conversation={conversationNode}
          context={contextNode}
          hasSelectedTicket={!!selectedTicket}
          activeTicketId={selectedTicket?.id ?? null}
          stripTickets={tickets.map(t => ({
            id: t.id,
            priority: t.priority,
            subject: t.subject,
            customer: t.customer_name_extracted || t.from_name || t.from_email || t.customer_email,
            createdAt: t.created_at
          }))}
          expanded={tabletExpanded}
          onExpandInbox={() => setTabletExpanded(true)}
          onSwitchTicket={t => selectTicket(t)}
        />
      ) : (
        <DesktopTicketsLayout
          containerRef={mainContainerRef}
          isMobile={isMobile}
          layoutHeight={layoutHeight}
          selectedTicket={selectedTicket}
          ticketDetails={ticketDetails}
          inbox={inboxNode}
          conversation={conversationNode}
          context={contextNode}
          onLeftResize={handleLeftResize}
          onRightResize={handleRightResize}
        />
      )}

      {/* Email Filters Modal */}
      {showFilterModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 900 }}>
            <div className="modal-header">
              <h2>Email Filters</h2>
              <button className="modal-close" onClick={() => setShowFilterModal(false)}>&times;</button>
            </div>
            <div className="modal-body-scroll" style={{ maxHeight: 400 }}>
              <p style={{ marginBottom: 16, color: 'var(--muted)' }}>
                Configure rules to block emails or route them to specific streams.
              </p>

              {/* Add Filter Form */}
              <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 100px 120px 1fr auto', gap: 8, marginBottom: 20, alignItems: 'end' }}>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--muted)' }}>Type</label>
                  <select
                    className="form-input"
                    value={newFilter.filter_type}
                    onChange={e => setNewFilter({ ...newFilter, filter_type: e.target.value })}
                  >
                    <option value="email">Email</option>
                    <option value="domain">Domain</option>
                    <option value="subject">Subject</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--muted)' }}>
                    Pattern {newFilter.filter_type === 'subject' && '(contains)'}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder={
                      newFilter.filter_type === 'domain' ? 'noreply.com' :
                      newFilter.filter_type === 'subject' ? 'shipping confirmation' :
                      'example@email.com'
                    }
                    value={newFilter.pattern}
                    onChange={e => setNewFilter({ ...newFilter, pattern: e.target.value })}
                  />
                </div>
                {newFilter.filter_type !== 'subject' && (
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--muted)' }}>Match</label>
                    <select
                      className="form-input"
                      value={newFilter.match_type}
                      onChange={e => setNewFilter({ ...newFilter, match_type: e.target.value })}
                    >
                      <option value="exact">Exact</option>
                      <option value="contains">Contains</option>
                      <option value="starts_with">Starts with</option>
                      <option value="ends_with">Ends with</option>
                    </select>
                  </div>
                )}
                {newFilter.filter_type === 'subject' && <div />}
                <div>
                  <label style={{ fontSize: 12, color: 'var(--muted)' }}>Action</label>
                  <select
                    className="form-input"
                    value={newFilter.action}
                    onChange={e => setNewFilter({ ...newFilter, action: e.target.value })}
                  >
                    <option value="block">Block (don't import)</option>
                    <option value="status:service">Set as Service</option>
                    <option value="status:offers">Set as Offers</option>
                    <option value="status:spam">Set as Spam</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--muted)' }}>Reason</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Optional note"
                    value={newFilter.reason}
                    onChange={e => setNewFilter({ ...newFilter, reason: e.target.value })}
                  />
                </div>
                <button className="btn btn-primary" onClick={addFilter}>Add</button>
              </div>

              {/* Compound Filter - Subject Condition (for Email/Domain filters) */}
              {newFilter.filter_type !== 'subject' && (
                <div style={{ marginBottom: 16, padding: 12, background: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0369a1', marginBottom: 8 }}>
                    + Optional: Add Subject Condition
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                    If set, both {newFilter.filter_type} AND subject must match
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 8 }}>
                    <div>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Subject contains... (leave blank for any)"
                        value={newFilter.subject_pattern}
                        onChange={e => setNewFilter({ ...newFilter, subject_pattern: e.target.value })}
                        style={{ fontSize: 12 }}
                      />
                    </div>
                    <div>
                      <select
                        className="form-input"
                        value={newFilter.subject_match_type}
                        onChange={e => setNewFilter({ ...newFilter, subject_match_type: e.target.value })}
                        style={{ fontSize: 12 }}
                      >
                        <option value="contains">Contains</option>
                        <option value="exact">Exact</option>
                        <option value="starts_with">Starts with</option>
                        <option value="ends_with">Ends with</option>
                      </select>
                    </div>
                  </div>
                  {newFilter.subject_pattern && (
                    <div style={{ marginTop: 8, fontSize: 11, color: '#059669', fontWeight: 500 }}>
                      Filter: {newFilter.filter_type} "{newFilter.pattern}" + subject {newFilter.subject_match_type} "{newFilter.subject_pattern}"
                    </div>
                  )}
                </div>
              )}

              {/* Filter List */}
              {filters.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>
                  No filters configured
                </div>
              ) : (
                <>
                  {/* Search input */}
                  <div style={{ marginBottom: 12 }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Search filters by pattern, reason, or type..."
                      value={filterSearch}
                      onChange={e => setFilterSearch(e.target.value)}
                      style={{ width: '100%' }}
                    />
                  </div>
                  {(() => {
                    const searchLower = filterSearch.toLowerCase()
                    const filteredFilters = filters.filter(f =>
                      !filterSearch ||
                      f.pattern?.toLowerCase().includes(searchLower) ||
                      f.reason?.toLowerCase().includes(searchLower) ||
                      f.filter_type?.toLowerCase().includes(searchLower) ||
                      f.action?.toLowerCase().includes(searchLower) ||
                      f.subject_pattern?.toLowerCase().includes(searchLower)
                    )
                    return filteredFilters.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>
                        No filters match "{filterSearch}"
                      </div>
                    ) : (
                      <table style={{ width: '100%', fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid rgba(0,0,0,.1)' }}>
                            <th style={{ textAlign: 'left', padding: 8 }}>Type</th>
                            <th style={{ textAlign: 'left', padding: 8 }}>Pattern</th>
                            <th style={{ textAlign: 'left', padding: 8 }}>Match</th>
                            <th style={{ textAlign: 'left', padding: 8 }}>Action</th>
                            <th style={{ textAlign: 'left', padding: 8 }}>Reason</th>
                            <th style={{ textAlign: 'left', padding: 8 }}>Added</th>
                            <th style={{ textAlign: 'left', padding: 8 }}>By</th>
                            <th style={{ textAlign: 'right', padding: 8 }}>Hits</th>
                            <th style={{ padding: 8 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredFilters.map(filter => (
                            <tr key={filter.id} style={{ borderBottom: '1px solid rgba(0,0,0,.06)' }}>
                              <td style={{ padding: 8 }}>
                                <span className="badge badge-secondary">{filter.filter_type}</span>
                                {filter.subject_pattern && (
                                  <span className="badge" style={{ marginLeft: 4, background: '#8B5CF6', color: 'white' }}>+subject</span>
                                )}
                              </td>
                              <td style={{ padding: 8, fontFamily: 'monospace' }}>
                                {filter.pattern}
                                {filter.subject_pattern && (
                                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                                    + subject {filter.subject_match_type || 'contains'}: "{filter.subject_pattern}"
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: 8 }}>{filter.filter_type === 'subject' ? 'contains' : filter.match_type}</td>
                              <td style={{ padding: 8 }}>
                                {filter.action === 'block' ? (
                                  <span className="badge" style={{ background: '#EF4444', color: 'white' }}>Block</span>
                                ) : filter.action === 'status:service' ? (
                                  <span className="badge" style={{ background: '#6B7280', color: 'white' }}>→ Service</span>
                                ) : filter.action === 'status:offers' ? (
                                  <span className="badge" style={{ background: '#8B5CF6', color: 'white' }}>→ Offers</span>
                                ) : filter.action === 'status:spam' ? (
                                  <span className="badge" style={{ background: '#F59E0B', color: 'white' }}>→ Spam</span>
                                ) : filter.action?.startsWith('status:') ? (
                                  <span className="badge" style={{ background: '#6B7280', color: 'white' }}>
                                    → {filter.action.replace('status:', '')}
                                  </span>
                                ) : (
                                  <span className="badge badge-secondary">{filter.action || 'block'}</span>
                                )}
                              </td>
                              <td style={{ padding: 8, color: 'var(--muted)' }}>{filter.reason || '-'}</td>
                              <td style={{ padding: 8, color: 'var(--muted)', whiteSpace: 'nowrap' }} title={filter.created_at || ''}>
                                {filter.created_at ? filter.created_at.slice(0, 10) : '—'}
                              </td>
                              <td style={{ padding: 8, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                                {(() => {
                                  if (!filter.created_by) return '—'
                                  const u = ticketUsers.find(u => String(u.user_id) === String(filter.created_by))
                                  return u?.name || `#${filter.created_by}`
                                })()}
                              </td>
                              <td style={{ padding: 8, textAlign: 'right' }}>{filter.hits}</td>
                              <td style={{ padding: 8, whiteSpace: 'nowrap' }}>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => applyFilterToExisting(filter.id)}
                                  title="Apply this filter to existing tickets"
                                  style={{ marginRight: 4 }}
                                >
                                  Apply
                                </button>
                                <button
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => deleteFilter(filter.id)}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  })()}
                  {filterSearch && (
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>
                      Showing {filters.filter(f =>
                        f.pattern?.toLowerCase().includes(filterSearch.toLowerCase()) ||
                        f.reason?.toLowerCase().includes(filterSearch.toLowerCase()) ||
                        f.filter_type?.toLowerCase().includes(filterSearch.toLowerCase()) ||
                        f.action?.toLowerCase().includes(filterSearch.toLowerCase()) ||
                        f.subject_pattern?.toLowerCase().includes(filterSearch.toLowerCase())
                      ).length} of {filters.length} filters
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowFilterModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Lookup Modal */}
      {customerEmail && (
        <CustomerModal
          email={customerEmail}
          onClose={() => setCustomerEmail(null)}
        />
      )}

      {/* Pin Ticket Modal */}
      {showPinModal && selectedTicket && (
        <div className="modalOverlay">
          <div className="modalContent" onClick={e => e.stopPropagation()} style={{ maxWidth: 450 }}>
            <div className="modalHead">
              <h2>Pin Ticket</h2>
              <button className="modalClose" onClick={() => setShowPinModal(false)}>&times;</button>
            </div>
            <div className="modalBody">
              <p style={{ marginBottom: 16, color: 'var(--muted)' }}>
                Pin this ticket for follow-up. It will appear in your pinned items list.
              </p>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                  Why are you pinning this? *
                </label>
                <textarea
                  className="form-input"
                  rows={3}
                  placeholder="e.g., Waiting for customer response, Need to follow up on refund..."
                  value={pinData.reason}
                  onChange={e => setPinData({ ...pinData, reason: e.target.value })}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                  Due Date (optional)
                </label>
                <input
                  type="date"
                  className="form-input"
                  value={pinData.dueDate}
                  onChange={e => setPinData({ ...pinData, dueDate: e.target.value })}
                />
              </div>
              {user?.is_admin && pinUsers.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                    Assign To
                  </label>
                  <select
                    className="form-input"
                    value={pinData.assignedTo}
                    onChange={e => setPinData({ ...pinData, assignedTo: e.target.value })}
                  >
                    <option value="">Myself (default)</option>
                    {pinUsers.map(u => (
                      <option key={u.user_id} value={u.user_id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="modalFoot">
              <button className="btn btn-secondary" onClick={() => setShowPinModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={pinTicket}
                disabled={!pinData.reason.trim()}
              >
                Pin Ticket
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Management Modal */}
      {showTemplateModal && (
        <div className="modalOverlay">
          <div className="modalContent" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: '90vh' }}>
            <div className="modalHead">
              <h2>Reply Templates</h2>
              <button className="modalClose" onClick={() => setShowTemplateModal(false)}>&times;</button>
            </div>
            <div className="modalBody" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <p style={{ marginBottom: 16, color: 'var(--muted)' }}>
                Create templates for common replies. AI will suggest these when it detects matching messages.
              </p>

              {/* Template Form */}
              <div style={{
                background: 'var(--bg-alt)',
                padding: 16,
                borderRadius: 8,
                marginBottom: 16,
                border: '1px solid var(--border)'
              }}>
                <h4 style={{ margin: '0 0 12px 0' }}>
                  {editingTemplate ? 'Edit Template' : 'New Template'}
                </h4>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>
                    Template Name
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Drop Off Hours"
                    value={templateForm.name}
                    onChange={e => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>
                    Keywords (comma separated)
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. drop off, opening hours, visit, warehouse"
                    value={templateForm.keywords}
                    onChange={e => setTemplateForm(prev => ({ ...prev, keywords: e.target.value }))}
                  />
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                    AI uses these to match incoming messages to this template
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>
                    Template Body
                  </label>
                  <textarea
                    className="form-input"
                    rows={5}
                    placeholder="Enter your template text here..."
                    value={templateForm.body}
                    onChange={e => setTemplateForm(prev => ({ ...prev, body: e.target.value }))}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {editingTemplate && (
                    <button className="btn btn-secondary btn-sm" onClick={cancelTemplateEdit}>
                      Cancel Edit
                    </button>
                  )}
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={saveTemplate}
                    disabled={savingTemplate}
                  >
                    {savingTemplate ? 'Saving...' : editingTemplate ? 'Update Template' : 'Add Template'}
                  </button>
                </div>
              </div>

              {/* Existing Templates */}
              <h4 style={{ margin: '0 0 12px 0' }}>Existing Templates</h4>
              {loadingTemplates ? (
                <div style={{ textAlign: 'center', padding: 20 }}>Loading...</div>
              ) : templates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>
                  No templates yet. Create one above.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {templates.map(template => (
                    <div
                      key={template.id}
                      style={{
                        padding: 12,
                        background: 'white',
                        border: '1px solid var(--border)',
                        borderRadius: 8
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div>
                          <strong>{template.name}</strong>
                          {template.keywords && (
                            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                              Keywords: {JSON.parse(template.keywords || '[]').join(', ')}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            className="btn btn-xs btn-secondary"
                            onClick={() => editTemplate(template)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-xs btn-secondary"
                            onClick={() => deleteTemplate(template.id)}
                            style={{ color: '#DC2626' }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <div style={{
                        fontSize: 13,
                        color: '#666',
                        whiteSpace: 'pre-wrap',
                        maxHeight: 80,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {template.body_text}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modalFoot">
              <button className="btn btn-secondary" onClick={() => setShowTemplateModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signature Modal */}
      {showTopicCounts && <TopicCountsModal onClose={() => setShowTopicCounts(false)} />}

      {showSignatureModal && (
        <div className="modalOverlay">
          <div className="modalContent" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modalHead">
              <h2>Email Signature</h2>
              <button className="modalClose" onClick={() => setShowSignatureModal(false)}>&times;</button>
            </div>
            <div className="modalBody">
              <p style={{ marginBottom: 16, color: 'var(--muted)' }}>
                This signature will be automatically appended to all ticket replies.
              </p>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                  Signature
                </label>
                <textarea
                  className="form-input"
                  rows={6}
                  placeholder="Enter your signature here...&#10;&#10;Example:&#10;Best regards,&#10;Your Name&#10;your@email.com"
                  value={signature}
                  onChange={e => setSignature(e.target.value)}
                  style={{ fontFamily: 'inherit' }}
                />
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                Tip: Use line breaks to format your signature. HTML is not supported.
              </div>
            </div>
            <div className="modalFoot">
              <button className="btn btn-secondary" onClick={() => setShowSignatureModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={saveSignature}
                disabled={savingSignature}
              >
                {savingSignature ? 'Saving...' : 'Save Signature'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Failures Modal */}
      {showSyncFailuresModal && (
        <div className="modalOverlay">
          <div className="modalContent" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div className="modalHead">
              <h2>Sync Failures</h2>
              <button className="modalClose" onClick={() => setShowSyncFailuresModal(false)}>&times;</button>
            </div>
            <div className="modalBody">
              {loadingSyncFailures ? (
                <p>Loading...</p>
              ) : syncFailures.length === 0 ? (
                <p style={{ color: 'var(--muted)' }}>No sync failures. All messages processed successfully.</p>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                    {syncFailureCounts.failed > 0 && (
                      <span style={{ background: '#FEF3C7', color: '#92400E', padding: '4px 10px', borderRadius: 12, fontSize: 13, fontWeight: 500 }}>
                        {syncFailureCounts.failed} failed (will retry)
                      </span>
                    )}
                    {syncFailureCounts.abandoned > 0 && (
                      <span style={{ background: '#FEE2E2', color: '#991B1B', padding: '4px 10px', borderRadius: 12, fontSize: 13, fontWeight: 500 }}>
                        {syncFailureCounts.abandoned} abandoned (max retries reached)
                      </span>
                    )}
                  </div>
                  <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    {syncFailures.map((f, i) => (
                      <div key={f.gmail_message_id} style={{
                        padding: '10px 12px',
                        borderBottom: '1px solid var(--stroke)',
                        fontSize: 13
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--muted)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {f.gmail_message_id}
                            </div>
                            {f.error_message && (
                              <div style={{ color: '#DC2626', marginBottom: 4 }}>{f.error_message}</div>
                            )}
                            <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                              Retries: {f.retry_count}/{f.max_retries}
                              {f.next_retry_at && f.status === 'failed' && ` · Next retry: ${formatDate(f.next_retry_at)}`}
                              {f.status === 'abandoned' && ' · Abandoned'}
                            </div>
                          </div>
                          {f.status === 'abandoned' && (
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => retrySyncFailures([f.gmail_message_id])}
                              disabled={retryingSyncFailures}
                              style={{ flexShrink: 0 }}
                            >
                              Retry
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="modalFoot">
              <button className="btn btn-secondary" onClick={() => setShowSyncFailuresModal(false)}>
                Close
              </button>
              {syncFailures.length > 0 && (
                <button
                  className="btn btn-primary"
                  onClick={() => retrySyncFailures()}
                  disabled={retryingSyncFailures}
                >
                  {retryingSyncFailures ? 'Retrying...' : 'Retry All'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Rewrite Modal */}
      {showRewriteModal && (
        <div className="modalOverlay">
          <div className="modalContent" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modalHead">
              <h2>AI Rewritten Message</h2>
              <button className="modalClose" onClick={cancelRewrite}>&times;</button>
            </div>
            <div className="modalBody">
              <p style={{ marginBottom: 16, color: 'var(--muted)' }}>
                Here's a polished version of your message. Click Accept to use it, or Cancel to keep your original.
              </p>
              <div style={{
                background: 'var(--bg-alt)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: 16,
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                lineHeight: 1.6,
                maxHeight: 400,
                overflowY: 'auto'
              }}>
                {rewrittenText}
              </div>
            </div>
            <div className="modalFoot">
              <button className="btn btn-secondary" onClick={cancelRewrite}>
                Cancel
              </button>
              <button
                className="btn btn-secondary"
                onClick={reportBadRewrite}
                disabled={rewriteReported}
                style={{ color: rewriteReported ? 'var(--muted)' : 'var(--danger, #e74c3c)' }}
              >
                {rewriteReported ? 'Reported' : 'Report Bad Response'}
              </button>
              <button className="btn btn-primary" onClick={acceptRewrite}>
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

      {/* Order detail modal opened from sidebar */}
      {sidebarOrderModal && (
        <OrderDetailModal
          order={sidebarOrderModal}
          onClose={() => setSidebarOrderModal(null)}
        />
      )}

      {/* Deal detail modal opened from sidebar */}
      {sidebarDealModal && (
        <DealDetailModal
          deal={sidebarDealModal}
          onClose={() => setSidebarDealModal(null)}
        />
      )}
    </>
  )
}

export default Tickets
